import { format, parseISO } from 'date-fns';
import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { SubscriptionDto } from '@/Domain/api/Subscription/dto/Subscription.dto';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/Domain/components/Shared/Card/Card';
import { CancelSubscriptionDialog } from '@/Domain/components/Subscription/CancelSubscription.dialog';
import { MembershipCardAction } from '@/Domain/components/Subscription/MembershipCard/MembershipCardAction';
import { SubscriptionHelper } from '@/Domain/helper/Subscription.helper';
import { useChangeSubscriptionStatus } from '@/Domain/hooks/Subscription/useChangeSubscriptionStatus.hook';

type Props = {
  currentPlan: SubscriptionDto['current_plan'];
  subscription: SubscriptionDto['subscription'];
};

export const MembershipCard = ({ currentPlan, subscription }: Props) => {
  const { t } = useTranslation('subscription_page');
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isActivating, setIsActivating] = useState(false);

  const { handleChangeSubscriptionStatus } = useChangeSubscriptionStatus({
    cancelCallback: () => setIsCancelDialogOpen(false),
    activeCallback: () => setIsActivating(false),
  });

  // Reverting a cancellation only clears `cancel_at_period_end`, so it needs no price (legacy tiers have none).
  const handleActivate = () => {
    setIsActivating(true);
    handleChangeSubscriptionStatus('activate').catch(() => setIsActivating(false));
  };

  const nextPayment = useMemo(() => {
    if (subscription.canceled) {
      return t('status.cancel');
    }

    return subscription.next_payment_on
      ? format(parseISO(subscription.next_payment_on), 'd MMMM, yyyy')
      : '-';
  }, [subscription.canceled, subscription.next_payment_on]);

  const selectedPlan = [
    SubscriptionHelper.getTrierLabel(currentPlan.tier, subscription.status),
    subscription.interval ? SubscriptionHelper.getIntervalLabel(subscription.interval) : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t('membership_card.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-between gap-4 text-sm">
            <div>
              <p className="text-foreground-subtle mb-1.5">{t('membership_card.plan_name')}</p>
              <span className="text-foreground font-medium">{selectedPlan}</span>
            </div>
            <div>
              <p className="text-foreground-subtle mb-1.5">{t('membership_card.next_payment')}</p>
              <span className="text-foreground font-medium">{nextPayment}</span>
            </div>
          </div>
        </CardContent>
        <CardFooter className="gap-4 mt-auto">
          <MembershipCardAction
            tier={currentPlan.tier}
            canceled={subscription.canceled}
            isActivating={isActivating}
            onCancelClick={() => setIsCancelDialogOpen(true)}
            onActivateClick={handleActivate}
          />
          {subscription.canceled && subscription.inactive_on && (
            <p className="text-sm text-foreground-subtle">
              <Trans
                i18nKey="subscription_page:membership_card.un_active_account_until"
                components={{
                  date: (
                    <span className="font-medium">
                      {format(new Date(subscription.inactive_on), 'dd MMM yyyy')}
                    </span>
                  ),
                }}
              />
            </p>
          )}
        </CardFooter>
      </Card>

      <CancelSubscriptionDialog
        isOpen={isCancelDialogOpen}
        onOpenChange={() => setIsCancelDialogOpen((prev) => !prev)}
        onCallback={(reason) => handleChangeSubscriptionStatus('cancel', reason)}
      />
    </>
  );
};
